import { SagaIterator } from 'redux-saga'
import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery,
  takeLatest,
  takeLeading
} from 'redux-saga/effects'
import { CONFIGURACAO_KANBAN, TYPES } from './types'
import * as configuracaoKanbanController from 'controller/configuracaoKanbanController'
import * as configuracaoKanbanActions from 'store/modules/backoffice/configuracaoKanban/actions'
import * as loadingActions from 'store/modules/loading/actions'
import { handlerError } from 'util/handlerError'
import { ApplicationState } from 'store'
import { toast } from 'react-toastify'

interface GenericProps {
  type: string
  id: string
}

interface GetByIdProps extends GenericProps {
  visualizacao: boolean
}

function* getConfiguracaoKanban(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(
      configuracaoKanbanController.getConfiguracaoKanban
    )
    yield put(
      configuracaoKanbanActions.setConfiguracaoKanban(response?.data?.content)
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getPipelinesTabEtapas(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(configuracaoKanbanController.getPipelines)
    yield put(
      configuracaoKanbanActions.setPipelinesTabEtapas(
        response?.data?.content || []
      )
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getEtapasPipelineTabEtapas({ id }: GenericProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(
      configuracaoKanbanController.getEtapasPipeline,
      id
    )
    yield put(
      configuracaoKanbanActions.setEtapasPipelineTabEtapas(
        response?.data?.content || []
      )
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getEtapaByIdTabEtapas({
  id,
  visualizacao
}: GetByIdProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    yield put(configuracaoKanbanActions.setVisualizacaoTabEtapas(visualizacao))
    const response = yield call(configuracaoKanbanController.getEtapaById, id)
    if (response?.data?.content) {
      yield put(
        configuracaoKanbanActions.setRegisterTabEtapas({
          ...response?.data?.content,
          etapasPermitidas:
            response?.data?.content?.etapasPermitidas?.map(
              (etapa: { nome: string; etapaId: string }) => {
                return etapa.etapaId
              }
            ) || []
        })
      )
    } else {
      yield put(configuracaoKanbanActions.cleanRegisterTabEtapas())
    }
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getEtapasPermitidasTabEtapas({ id }: GenericProps): SagaIterator {
  try {
    const response = yield call(
      configuracaoKanbanController.getEtapasPipeline,
      id
    )

    yield put(
      configuracaoKanbanActions.setEtapasPermitidasTabEtapas(
        response?.data?.content || []
      )
    )
    yield put(
      configuracaoKanbanActions.setInvalidsFieldsRegisterTabEtapas(
        response?.data?.content?.map(() => {
          return {
            invalid: false,
            message: ''
          }
        }) || [],
        'etapasPermitidas'
      )
    )
  } catch (error) {
    handlerError(error)
  }
}

function* salvarEtapaTabEtapas(): SagaIterator {
  const { tabEtapas }: CONFIGURACAO_KANBAN = yield select(
    (state: ApplicationState) => state.configuracaoKanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.salvarEtapa, {
      pipelineId:
        tabEtapas.register?.pipelineId || tabEtapas.pipelineSelecionado || '',
      nome: tabEtapas.register?.nome || '',
      descricao: tabEtapas.register?.descricao || '',
      cor: tabEtapas.register?.cor || '',
      etapasPermitidas: tabEtapas.register?.etapasPermitidas || [],
      status: !!tabEtapas.register?.status
    })
    yield put(configuracaoKanbanActions.cleanRegisterTabEtapas())
    toast.success('Etapa salva com sucesso!')
    yield put(configuracaoKanbanActions.getPipelinesTabEtapasSaga())
    yield put(
      configuracaoKanbanActions.setEtapaPipelineSelecionadoTabEtapas(null)
    )
    yield put(
      configuracaoKanbanActions.getEtapasPipelineTabEtapasSaga(
        tabEtapas.pipelineSelecionado || tabEtapas.register?.pipelineId || ''
      )
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* editarEtapaTabEtapas(): SagaIterator {
  const { tabEtapas }: CONFIGURACAO_KANBAN = yield select(
    (state: ApplicationState) => state.configuracaoKanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.editarEtapa, {
      id: tabEtapas.register?.id || '',
      pipelineId:
        tabEtapas.pipelineSelecionado || tabEtapas.register?.pipelineId || '',
      nome: tabEtapas.register?.nome || '',
      descricao: tabEtapas.register?.descricao || '',
      cor: tabEtapas.register?.cor || '',
      etapasPermitidas: tabEtapas.register?.etapasPermitidas || [],
      status: !!tabEtapas.register?.status
    })
    yield put(configuracaoKanbanActions.cleanRegisterTabEtapas())
    toast.success('Etapa editada com sucesso!')
    yield put(configuracaoKanbanActions.getPipelinesTabEtapasSaga())
    yield put(
      configuracaoKanbanActions.setEtapaPipelineSelecionadoTabEtapas(null)
    )
    yield put(
      configuracaoKanbanActions.getEtapasPipelineTabEtapasSaga(
        tabEtapas.pipelineSelecionado || tabEtapas.register?.pipelineId || ''
      )
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* removerEtapaTabEtapas({ id }: GenericProps): SagaIterator {
  const { tabEtapas }: CONFIGURACAO_KANBAN = yield select(
    (state: ApplicationState) => state.configuracaoKanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.removerEtapa, id)
    toast.success('Etapa removida com sucesso!')
    yield put(configuracaoKanbanActions.getPipelinesTabEtapasSaga())
    yield put(
      configuracaoKanbanActions.setEtapaPipelineSelecionadoTabEtapas(null)
    )
    yield put(
      configuracaoKanbanActions.getEtapasPipelineTabEtapasSaga(
        tabEtapas.pipelineSelecionado || tabEtapas.register?.pipelineId || ''
      )
    )
    if (tabEtapas.register?.id === id) {
      yield put(configuracaoKanbanActions.cleanRegisterTabEtapas())
    }
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

interface AlterarOrdemProps extends GenericProps {
  ordem: number
}
function* alterarOrdemEtapa({ id, ordem }: AlterarOrdemProps): SagaIterator {
  const { tabEtapas }: CONFIGURACAO_KANBAN = yield select(
    (state: ApplicationState) => state.configuracaoKanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.alterarOrdemEtapa, {
      id,
      ordem
    })
    toast.success('Ordem da etapa alterada com sucesso!')
    yield put(configuracaoKanbanActions.getPipelinesTabEtapasSaga())
    yield put(
      configuracaoKanbanActions.setEtapaPipelineSelecionadoTabEtapas(null)
    )
    yield put(
      configuracaoKanbanActions.getEtapasPipelineTabEtapasSaga(
        tabEtapas.pipelineSelecionado || tabEtapas.register?.pipelineId || ''
      )
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getBigNumbersTabSLAs(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(configuracaoKanbanController.getBigNumbers)
    if (response?.data?.content) {
      yield put(
        configuracaoKanbanActions.setBignumbersTabSLAs(response?.data?.content)
      )
    }
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getSlaByIdTabSLAs({ id, visualizacao }: GetByIdProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    yield put(configuracaoKanbanActions.setVisualizacaoTabSLAs(visualizacao))
    const response = yield call(configuracaoKanbanController.getSlaById, id)
    if (response?.data?.content) {
      yield put(
        configuracaoKanbanActions.setRegisterTabSLAs(response?.data?.content)
      )
      yield put(
        configuracaoKanbanActions.setInvalidsRegisterTabSLAs({
          nome: { invalid: false, message: '' },
          descricao: { invalid: false, message: '' },
          prazo: { invalid: false, message: '' },
          pipelineId: { invalid: false, message: '' },
          pipelineEtapaId: { invalid: false, message: '' },
          regrasEscalacao:
            response?.data?.content?.regrasEscalacao?.map(() => {
              return {
                disparaEm: { invalid: false, message: '' },
                acaoId: { invalid: false, message: '' },
                mensagem: { invalid: false, message: '' }
              }
            }) || [],
          regrasEscalacaoMessage: { invalid: false, message: '' }
        })
      )
    } else {
      yield put(configuracaoKanbanActions.cleanRegisterTabSLAs())
    }
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getPipelinesTabSLAs(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(configuracaoKanbanController.getPipelines)
    yield put(
      configuracaoKanbanActions.setPipelinesTabSLAs(
        response?.data?.content || []
      )
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getEtapasTabSLAs(): SagaIterator {
  const { tabSLAs }: CONFIGURACAO_KANBAN = yield select(
    (state: ApplicationState) => state.configuracaoKanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(
      configuracaoKanbanController.getEtapasPipeline,
      tabSLAs.register?.pipelineId || ''
    )
    yield put(
      configuracaoKanbanActions.setEtapasTabSLAs(response?.data?.content || [])
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getAcoesTabSLAs(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(configuracaoKanbanController.getAcoes)
    if (response?.data?.content) {
      yield put(
        configuracaoKanbanActions.setAcoesTabSLAs(response?.data?.content)
      )
    }
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* salvarSlaTabSLAs(): SagaIterator {
  const { tabSLAs }: CONFIGURACAO_KANBAN = yield select(
    (state: ApplicationState) => state.configuracaoKanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.salvarSla, {
      id: tabSLAs.register?.id,
      etapaId: tabSLAs.register?.pipelineEtapaId || '',
      nome: tabSLAs.register?.nome || '',
      descricao: tabSLAs.register?.descricao || '',
      prazo: tabSLAs.register?.prazo || 0,
      regrasEscalacao: tabSLAs.register?.regrasEscalacao || []
    })
    yield put(configuracaoKanbanActions.cleanRegisterTabSLAs())
    toast.success('SLA salvo com sucesso!')
    yield call(getBigNumbersTabSLAs)
    yield call(getSLAsConfiguradosTabSLAs)
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* editarSlaTabSLAs(): SagaIterator {
  const { tabSLAs }: CONFIGURACAO_KANBAN = yield select(
    (state: ApplicationState) => state.configuracaoKanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.editarSla, {
      id: tabSLAs.register?.id,
      etapaId: tabSLAs.register?.pipelineEtapaId || '',
      nome: tabSLAs.register?.nome || '',
      descricao: tabSLAs.register?.descricao || '',
      prazo: tabSLAs.register?.prazo || 0,
      regrasEscalacao: tabSLAs.register?.regrasEscalacao || []
    })
    yield put(configuracaoKanbanActions.cleanRegisterTabSLAs())
    toast.success('SLA editado com sucesso!')
    yield call(getSLAsConfiguradosTabSLAs)
    yield call(getBigNumbersTabSLAs)
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getSLAsConfiguradosTabSLAs(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(
      configuracaoKanbanController.getSLAsConfigurados
    )
    if (response?.data?.content) {
      yield put(
        configuracaoKanbanActions.setSLAsConfiguradosTabSLAs(
          response?.data?.content
        )
      )
    }
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* removerSlaConfiguradoTabSLAs({ id }: GenericProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.removerSlaConfigurado, id)
    toast.success('SLA removido com sucesso!')
    yield call(getSLAsConfiguradosTabSLAs)
    yield call(getBigNumbersTabSLAs)
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getPipelinesTabPipelines(): SagaIterator {
  const { tabPipelines }: CONFIGURACAO_KANBAN = yield select(
    (state: ApplicationState) => state.configuracaoKanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(
      configuracaoKanbanController.getPipelinesPaginado,
      {
        pagina: tabPipelines.pagination.page,
        registros: tabPipelines.pagination.pageSize,
        nome: tabPipelines.search.nome,
        status: tabPipelines.search.status
      }
    )
    yield put(
      configuracaoKanbanActions.setPipelinesTabPipelines(
        response?.data?.content?.registros || []
      )
    )
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getPipelineByIdTabPipelines({ id }: GenericProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(
      configuracaoKanbanController.getPipelineById,
      id
    )
    if (response?.data?.content) {
      yield put(
        configuracaoKanbanActions.setRegisterTabPipelines({
          id: response?.data?.content?.id,
          nomePipeline: response?.data?.content?.nome,
          descricao: response?.data?.content?.descricao,
          centroCustoId: response?.data?.content?.centroCustoId,
          quantidadeLeads: response?.data?.content?.quantidadeLeads,
          site: response?.data?.content?.site
        })
      )
    }
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* editarPipelineTabPipelines(): SagaIterator {
  const { tabPipelines }: CONFIGURACAO_KANBAN = yield select(
    (state: ApplicationState) => state.configuracaoKanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.editarPipeline, {
      id: tabPipelines.register?.id || '',
      nome: tabPipelines.register?.nomePipeline || '',
      descricao: tabPipelines.register?.descricao || '',
      centroCustoId: tabPipelines.register?.centroCustoId || '',
      quantidadeLeads: tabPipelines.register?.quantidadeLeads || 100,
      site: tabPipelines.register?.site || false
    })
    yield put(configuracaoKanbanActions.cleanRegisterTabPipelines())
    toast.success('Pipeline editado com sucesso!')
    yield call(getPipelinesTabPipelines)
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* salvarPipelineTabPipelines(): SagaIterator {
  const { tabPipelines }: CONFIGURACAO_KANBAN = yield select(
    (state: ApplicationState) => state.configuracaoKanban
  )
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.salvarPipeline, {
      nome: tabPipelines.register?.nomePipeline || '',
      descricao: tabPipelines.register?.descricao || '',
      centroCustoId: tabPipelines.register?.centroCustoId || '',
      quantidadeLeads: tabPipelines.register?.quantidadeLeads || 100,
      site: tabPipelines.register?.site || false
    })
    yield put(configuracaoKanbanActions.cleanRegisterTabPipelines())
    toast.success('Pipeline salvo com sucesso!')
    yield call(getPipelinesTabPipelines)
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* removerPipelineTabPipelines({ id }: GenericProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.removerPipeline, id)
    toast.success('Pipeline removido com sucesso!')
    yield call(getPipelinesTabPipelines)
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

interface AlterarStatusPipelineProps extends GenericProps {
  status: boolean
}
function* alterarStatusPipelineTabPipelines({
  id,
  status
}: AlterarStatusPipelineProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.alterarStatusPipeline, id, status)
    toast.success('Status do pipeline alterado com sucesso!')
    yield call(getPipelinesTabPipelines)
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* duplicarPipelineTabPipelines({ id }: GenericProps): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    yield call(configuracaoKanbanController.duplicarPipeline, id)
    toast.success('Pipeline duplicado com sucesso!')
    yield call(getPipelinesTabPipelines)
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* getCentrosCustoTabPipelines(): SagaIterator {
  try {
    yield put(loadingActions.setLoading(true))
    const response = yield call(configuracaoKanbanController.getCentrosCusto)
    if (response?.data?.content) {
      yield put(
        configuracaoKanbanActions.setOptionsSelectTabPipelines(
          response.data.content,
          'centrosCusto'
        )
      )
    }
  } catch (error) {
    handlerError(error)
  } finally {
    yield put(loadingActions.setLoading(false))
  }
}

function* watchGetConfiguracaoKanban() {
  yield takeLatest(TYPES.GET_CONFIGURACAO_KANBAN_SAGA, getConfiguracaoKanban)
}

function* watchGetPipelinesTabEtapas() {
  yield takeLatest(TYPES.GET_PIPELINES_TAB_ETAPAS_SAGA, getPipelinesTabEtapas)
}

function* watchGetEtapasPipelineTabEtapas() {
  yield takeLatest(
    TYPES.GET_ETAPAS_PIPELINE_TAB_ETAPAS_SAGA,
    getEtapasPipelineTabEtapas
  )
}

function* watchGetEtapaByIdTabEtapas() {
  yield takeLatest(TYPES.GET_ETAPA_BY_ID_TAB_ETAPAS_SAGA, getEtapaByIdTabEtapas)
}

function* watchGetEtapasPermitidasTabEtapas() {
  yield takeLatest(
    TYPES.GET_ETAPAS_PERMITIDAS_TAB_ETAPAS_SAGA,
    getEtapasPermitidasTabEtapas
  )
}

function* watchSalvarEtapaTabEtapas() {
  yield takeLeading(TYPES.SALVAR_ETAPA_TAB_ETAPAS_SAGA, salvarEtapaTabEtapas)
}

function* watchEditarEtapaTabEtapas() {
  yield takeLeading(TYPES.EDITAR_ETAPA_TAB_ETAPAS_SAGA, editarEtapaTabEtapas)
}

function* watchRemoverEtapaTabEtapas() {
  yield takeLeading(TYPES.REMOVER_ETAPA_TAB_ETAPAS_SAGA, removerEtapaTabEtapas)
}

function* watchAlterarOrdemEtapaTabEtapas() {
  yield takeLeading(
    TYPES.ALTERAR_ORDEM_ETAPA_TAB_ETAPAS_SAGA,
    alterarOrdemEtapa
  )
}

function* watchGetBigNumbersTabSLAs() {
  yield takeEvery(TYPES.GET_BIGNUMBERS_TAB_SLAS_SAGA, getBigNumbersTabSLAs)
}

function* watchGetSlaByIdTabSLAs() {
  yield takeEvery(TYPES.GET_SLA_BY_ID_TAB_SLAS_SAGA, getSlaByIdTabSLAs)
}

function* watchGetPipelinesTabSLAs() {
  yield takeEvery(TYPES.GET_PIPELINES_TAB_SLAS_SAGA, getPipelinesTabSLAs)
}

function* watchGetEtapasTabSLAs() {
  yield takeEvery(TYPES.GET_ETAPAS_TAB_SLAS_SAGA, getEtapasTabSLAs)
}

function* watchGetAcoesTabSLAs() {
  yield takeEvery(TYPES.GET_ACOES_TAB_SLAS_SAGA, getAcoesTabSLAs)
}

function* watchSalvarSlaTabSLAs() {
  yield takeLeading(TYPES.SALVAR_SLA_TAB_SLAS_SAGA, salvarSlaTabSLAs)
}

function* watchEditarSlaTabSLAs() {
  yield takeLeading(TYPES.EDITAR_SLA_TAB_SLAS_SAGA, editarSlaTabSLAs)
}

function* watchGetSLAsConfiguradosTabSLAs() {
  yield takeEvery(
    TYPES.GET_SLAS_CONFIGURADOS_TAB_SLAS_SAGA,
    getSLAsConfiguradosTabSLAs
  )
}

function* watchRemoverSlaConfiguradoTabSLAs() {
  yield takeLeading(
    TYPES.REMOVER_SLA_CONFIGURADO_TAB_SLAS_SAGA,
    removerSlaConfiguradoTabSLAs
  )
}

function* watchGetPipelinesTabPipelines() {
  yield takeEvery(
    TYPES.GET_PIPELINES_TAB_PIPELINES_SAGA,
    getPipelinesTabPipelines
  )
}

function* watchGetPipelineByIdTabPipelines() {
  yield takeEvery(
    TYPES.GET_PIPELINE_BY_ID_TAB_PIPELINES_SAGA,
    getPipelineByIdTabPipelines
  )
}

function* watchEditarPipelineTabPipelines() {
  yield takeLeading(
    TYPES.EDITAR_PIPELINE_TAB_PIPELINES_SAGA,
    editarPipelineTabPipelines
  )
}

function* watchSalvarPipelineTabPipelines() {
  yield takeLeading(
    TYPES.SALVAR_PIPELINE_TAB_PIPELINES_SAGA,
    salvarPipelineTabPipelines
  )
}

function* watchRemoverPipelineTabPipelines() {
  yield takeLeading(
    TYPES.REMOVER_PIPELINE_TAB_PIPELINES_SAGA,
    removerPipelineTabPipelines
  )
}

function* watchAlterarStatusPipelineTabPipelines() {
  yield takeEvery(
    TYPES.ALTERAR_STATUS_PIPELINE_TAB_PIPELINES_SAGA,
    alterarStatusPipelineTabPipelines
  )
}

function* watchDuplicarPipelineTabPipelines() {
  yield takeEvery(
    TYPES.DUPLICAR_PIPELINE_TAB_PIPELINES_SAGA,
    duplicarPipelineTabPipelines
  )
}

function* watchGetCentrosCustoTabPipelines() {
  yield takeEvery(
    TYPES.GET_CENTROS_CUSTO_TAB_PIPELINES_SAGA,
    getCentrosCustoTabPipelines
  )
}

function* configuracaoKanbanSaga() {
  yield all([
    fork(watchGetConfiguracaoKanban),
    fork(watchGetPipelinesTabEtapas),
    fork(watchGetEtapasPipelineTabEtapas),
    fork(watchGetEtapaByIdTabEtapas),
    fork(watchGetEtapasPermitidasTabEtapas),
    fork(watchSalvarEtapaTabEtapas),
    fork(watchEditarEtapaTabEtapas),
    fork(watchRemoverEtapaTabEtapas),
    fork(watchAlterarOrdemEtapaTabEtapas),
    fork(watchGetBigNumbersTabSLAs),
    fork(watchGetSlaByIdTabSLAs),
    fork(watchGetPipelinesTabSLAs),
    fork(watchGetEtapasTabSLAs),
    fork(watchGetAcoesTabSLAs),
    fork(watchSalvarSlaTabSLAs),
    fork(watchEditarSlaTabSLAs),
    fork(watchGetSLAsConfiguradosTabSLAs),
    fork(watchRemoverSlaConfiguradoTabSLAs),
    fork(watchGetPipelinesTabPipelines),
    fork(watchGetPipelineByIdTabPipelines),
    fork(watchEditarPipelineTabPipelines),
    fork(watchSalvarPipelineTabPipelines),
    fork(watchRemoverPipelineTabPipelines),
    fork(watchAlterarStatusPipelineTabPipelines),
    fork(watchDuplicarPipelineTabPipelines),
    fork(watchGetCentrosCustoTabPipelines)
  ])
}

export default configuracaoKanbanSaga
